import type { CommercialOrderGetters } from './types';

export function useCommercialOrderGetters(): CommercialOrderGetters {
  /**
   * Get the order ID
   * @param order - The order object
   * @returns The order ID
   */
  const getOrderId = (order: CommercialOrder): string => order.id;

  /**
   * Get the order creation date
   * @param order - The order object
   * @returns The order creation date
   */
  const getOrderCreatedAt = (order: CommercialOrder): string => order.createdAt;

  /**
   * Get the order reference
   * @param order - The order object
   * @returns The order reference
   */
  const getOrderReference = (order: CommercialOrder): string => order.reference;

  /**
   * Get the total shipping fees for the order
   * @param order - The order object
   * @returns Total shipping fees
   */
  const getOrderTotalShippingFees = (order: CommercialOrder): number =>
    order.orderLogisticPrices?.totalShippingFees;

  /**
   * Get the total item price for the order
   * @param order - The order object
   * @returns Total item price
   */
  const getOrderTotalItemPrice = (order: CommercialOrder): number =>
    order.orderLogisticPrices?.totalItemPrice;

  /**
   * Get the total order price
   * @param order - The order object
   * @returns Total order price
   */
  const getOrderTotalPrice = (order: CommercialOrder): number =>
    order.orderLogisticPrices?.totalOrderPrice;

  /**
   * Get the total order price without tax
   * @param order - The order object
   * @returns Total order price ht
   */
  const getOrderTotalPriceWithoutTax = (order: CommercialOrder): number =>
    order.orderLogisticPrices?.totalPriceWithoutTax;

  /**
   * Get the total tax amount for the order
   * @param order - The order object
   * @returns Total tax amount
   */
  const getOrderTotalTaxAmount = (order: CommercialOrder): number =>
    order.orderLogisticPrices?.totalTaxAmount;

  /**
   * Get the order logistics
   * @param order - The order object
   * @returns Logistics data for the order
   */
  const getOrderLogistics = (order: CommercialOrder): LogisticOrder[] => order.orderLogistics;

  /**
   * Check if the order is processing
   * @param order - The order object
   * @returns Processing status of the order
   */
  const isOrderProcessing = (order: CommercialOrder): boolean => order.processing;

  /**
   *  Get First OrderLogistic
   */
  const getFirstOrderLogistic = (order: CommercialOrder): LogisticOrder => order.orderLogistics[0];

  return {
    getOrderId,
    getOrderCreatedAt,
    getOrderReference,
    getOrderTotalShippingFees,
    getOrderTotalItemPrice,
    getOrderTotalPrice,
    getOrderTotalPriceWithoutTax,
    getOrderTotalTaxAmount,
    getOrderLogistics,
    isOrderProcessing,
    getFirstOrderLogistic,
  };
}
